import { alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Control, FieldValues, UseFormSetValue, useWatch } from 'react-hook-form';

import Label from '~components/FormFields/v2/Label';
import NumberInput from '~components/FormFields/v2/NumberInput';
import RateAndTypeInput from '~components/FormFields/v2/RateAndTypeInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import PayCommissionControl from '~components/PayCommission/PayCommissionControl';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { resolveDriverPayRate } from '~hooks/useSiteRateSchedule/driverPayRate';
import AddOnAssignmentCardV2 from '~pages/Sales/Orders/NewOrderForm/components/AddOnAssignmentCardV2';
import {
  AddOnAssignmentSchema,
  constructDefaultValues,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import AutomaticRateAddOnDialog from '~pages/Sales/Orders/NewOrderForm/components/AutomaticRateAddOnDialog';
import useRateDetails from '~pages/Sales/Orders/NewOrderForm/useRateDetails';
import { SelectRateCardDialog } from '~pages/Sales/Projects/components/SelectRateCardDialog';
import { useVendorRateForDriverToggle } from '~pages/Sales/Projects/components/vendorRateForDriver';
import { QUANTITY_AND_TIME_RATE_TYPES } from '~pages/Settings/RatesManagement/rateUtils';
import connection from '~services/connectionModule';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface FreightRatesSectionProps {
  control: Control<FieldValues>;
  index: number;
  setValue: UseFormSetValue<FieldValues>;
  accountId?: string;
  projectId?: string;
  // The order's service date (`YYYY-MM-DD`) the fuel-surcharge add-on rows
  // resolve their displayed rate at, matching the date billing stamps FSC
  // with (the job's start date). Absent on the project form, where the rows
  // fall back to today's rate.
  orderDate?: string;
  // Only the project scope quotes a quantity.
  scope?: 'order' | 'project';
  companyId?: string;
}

const rateLinksRowSx = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.5,
  justifyContent: 'space-between',
  mt: 0.5,
} as const;

const countActiveAddOns = (addOns: AddOnAssignmentSchema[]) =>
  addOns.filter((addOn) => !addOn.isDeleted).length;

const SurchargesAndAddOnsLink = ({
  activeAddOnCount,
  dataTestId,
  onClick,
}: {
  activeAddOnCount: number;
  dataTestId: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const theme = useTheme();

  return (
    <PseudoLink
      action={onClick}
      dataTestId={dataTestId}
      linkColor={theme.semantic.linkText}
      title={
        activeAddOnCount > 0
          ? t('project.form.surcharges_and_addons_link_with_count', {
              count: activeAddOnCount,
            })
          : t('project.form.surcharges_and_addons_link')
      }
      underline={false}
      variant="action"
    />
  );
};

const FreightRatesSection = ({
  control,
  index,
  setValue,
  accountId,
  projectId,
  orderDate,
  scope = 'order',
  companyId: formCompanyId,
}: FreightRatesSectionProps) => {
  const theme = useTheme();

  // Per-rate 3-dot menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRateOwnerType, setMenuRateOwnerType] = useState<RateOwnerType | null>(null);

  const [isSelectRateCardDialogOpen, setIsSelectRateCardDialogOpen] = useState(false);
  const handleCloseSelectRateCardDialog = () => setIsSelectRateCardDialogOpen(false);

  const { userStore } = useStores();
  const companyId = formCompanyId || getEffectiveUserCompanyId(userStore);
  const siteRateId = useWatch({ control, name: `siteRates.${index}.id` });

  const { fetchCustomerRates } = useRateDetails({
    accountId,
    companyId: companyId,
    projectId,
  });
  const { fetchVendorRates, fetchDriverRates } = useRateDetails({
    companyId: companyId,
    projectId,
  });

  const [selectedRateOwnerType, setSelectedRateOwnerType] =
    useState<RateOwnerType | null>(null);
  const [isAddOnDialogOpen, setIsAddOnDialogOpen] = useState(false);

  const customerAddOns =
    useWatch({
      control,
      name: `siteRates.${index}.customerAddOns`,
    }) || [];

  const vendorAddOns =
    useWatch({
      control,
      name: `siteRates.${index}.vendorAddOns`,
    }) || [];

  const driverAddOns =
    useWatch({
      control,
      name: `siteRates.${index}.driverAddOns`,
    }) || [];

  const { useVendorRateForDriver, handleToggleVendorRateForDriver } =
    useVendorRateForDriverToggle({ control, index });

  const customerRate = useWatch({
    control,
    name: `siteRates.${index}.customerRate`,
  });

  const vendorRate = useWatch({
    control,
    name: `siteRates.${index}.vendorRate`,
  });

  const driverRate = useWatch({ control, name: `siteRates.${index}.driverRate` });
  const [inheritedVendorCommission, inheritedDriverCommission] = useWatch({
    control,
    name: [
      `siteRates.${index}.inheritedVendorPayCommissionPercentage`,
      `siteRates.${index}.inheritedDriverPayCommissionPercentage`,
    ],
  });
  const netVendorRate = useWatch({ control, name: `siteRates.${index}.netVendorRate` });
  const netDriverRate = useWatch({ control, name: `siteRates.${index}.netDriverRate` });
  const driverPayRate = resolveDriverPayRate({
    useVendorRateForDriver,
    vendorRate,
    driverRate,
  });
  const initialVendorRate = useRef({
    gross: vendorRate?.rate,
    type: vendorRate?.type?.value,
    net: netVendorRate,
  }).current;
  const initialDriverPayRate = useRef({
    gross: driverPayRate?.rate,
    type: driverPayRate?.type?.value,
    net: netDriverRate,
  }).current;

  // Picking the customer rate's Type defaults (and keeps syncing) the
  // vendor rate's Type to match, since they're the same unit in the
  // overwhelming majority of cases — the user shouldn't have to pick or
  // update it twice. Only fires on a genuine post-mount CHANGE (the ref
  // starts at the current value, per HOOK-14/15), so opening an existing
  // rate card never re-triggers it.
  const previousCustomerRateTypeValueRef = useRef(customerRate?.type?.value);

  // Tracks the vendor type value this effect itself last wrote, so the
  // "did the user take manual control" check below can tell its own
  // writes apart from the user's. Cascading continues on every customer
  // type change until the vendor type changes to something this effect
  // didn't just set — that's the user picking (or clearing) it directly,
  // and from then on the two rates are permanently decoupled for this
  // card.
  const lastSyncedVendorTypeValueRef = useRef<string | undefined>(
    vendorRate?.type?.value,
  );
  // A card can mount with the vendor type already set — a previously-saved
  // SRS, not a value this effect is about to write. Starting this `false`
  // unconditionally would treat that as "still in sync" (even when it
  // happens to already match the customer type) and let the very next
  // customer type change silently overwrite a persisted vendor unit the
  // effect never chose. The cascade is only meant to help fill in a
  // genuinely blank vendor type — once one exists from any source, this
  // card is independent from mount.
  const vendorTypeIsIndependentRef = useRef(Boolean(vendorRate?.type?.value));

  useEffect(() => {
    const vendorTypeValue = vendorRate?.type?.value;
    if (vendorTypeValue === lastSyncedVendorTypeValueRef.current) return;
    vendorTypeIsIndependentRef.current = true;
  }, [vendorRate?.type?.value]);

  useEffect(() => {
    const customerTypeValue = customerRate?.type?.value;
    if (customerTypeValue === previousCustomerRateTypeValueRef.current) return;
    previousCustomerRateTypeValueRef.current = customerTypeValue;

    if (!customerTypeValue || vendorTypeIsIndependentRef.current) return;

    lastSyncedVendorTypeValueRef.current = customerTypeValue;
    setValue(
      `siteRates.${index}.vendorRate`,
      { ...vendorRate, type: customerRate.type },
      { shouldDirty: true },
    );
  }, [customerRate?.type, index, setValue, vendorRate]);

  const fetchRateAddOnAssignments = useCallback(
    async (
      rateId: string,
      rateOwnerType: RateOwnerType,
      currentAddOns: AddOnAssignmentSchema[],
    ) => {
      try {
        const response = await getV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments({
          path: { 'company-id': companyId, 'rate-id': rateId },
        });

        const addOnAssignments = response.data.data.map((addOnAssignment) =>
          AddOnAssignment.parse(addOnAssignment),
        );

        const automaticAddOnsDTO = constructDefaultValues(addOnAssignments, true, false);

        const currentAddOnsField = `${rateOwnerType.toLowerCase()}AddOns` as
          | 'customerAddOns'
          | 'vendorAddOns'
          | 'driverAddOns';

        const updatedAddOns = [
          ...currentAddOns.map((addOn: AddOnAssignmentSchema) => ({
            ...addOn,
            isDeleted: true,
          })),
          ...automaticAddOnsDTO.addOns,
        ];

        setValue(`siteRates.${index}.${currentAddOnsField}`, updatedAddOns);
      } catch (error) {
        connection.handleRequestError(error);
      }
    },
    [companyId, index, setValue],
  );

  const fetchRates = useCallback(
    (args?: { query: string; cursor: string }) => {
      if (!selectedRateOwnerType) {
        return Promise.resolve([]);
      }

      switch (selectedRateOwnerType) {
        case RateOwnerType.CUSTOMER:
          return fetchCustomerRates(args);
        case RateOwnerType.VENDOR:
          return fetchVendorRates(args);
        case RateOwnerType.DRIVER:
          return fetchDriverRates(args);
        default:
          throw new Error(`Unsupported rate owner type: ${selectedRateOwnerType}`);
      }
    },
    [fetchCustomerRates, fetchDriverRates, fetchVendorRates, selectedRateOwnerType],
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    rateOwnerType: RateOwnerType,
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuRateOwnerType(rateOwnerType);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRateOwnerType(null);
  };

  const handleAddOnFromMenu = () => {
    if (menuRateOwnerType) {
      setSelectedRateOwnerType(menuRateOwnerType);
      setIsAddOnDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleRateCardFromMenu = () => {
    if (menuRateOwnerType) {
      setSelectedRateOwnerType(menuRateOwnerType);
      setIsSelectRateCardDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleAddOnDialogOpen = (rateOwnerType: RateOwnerType) => {
    setSelectedRateOwnerType(rateOwnerType);
    setIsAddOnDialogOpen(true);
  };

  const handleAddOnDialogClose = () => {
    setIsAddOnDialogOpen(false);
    setSelectedRateOwnerType(null);
  };

  const handleAddOnSave = (automaticAddOns: { addOns: AddOnAssignmentSchema[] }) => {
    const fieldName = `${selectedRateOwnerType?.toLowerCase()}AddOns` as
      | 'customerAddOns'
      | 'vendorAddOns'
      | 'driverAddOns';
    setValue(`siteRates.${index}.${fieldName}`, automaticAddOns.addOns);
    handleAddOnDialogClose();
  };

  const handleRateSelect = (rate: RateBasicWithValue | null) => {
    if (rate && selectedRateOwnerType) {
      const rateFieldName = `${selectedRateOwnerType.toLowerCase()}Rate` as
        | 'customerRate'
        | 'vendorRate'
        | 'driverRate';

      setValue(`siteRates.${index}.${rateFieldName}`, {
        id: rate.id,
        rate: rate.rate,
        type: {
          value: rate.type,
          label: rate.readableTypeValue,
        },
      });

      if (rate.id) {
        let currentAddOns: AddOnAssignmentSchema[];
        switch (selectedRateOwnerType) {
          case RateOwnerType.CUSTOMER:
            currentAddOns = customerAddOns;
            break;
          case RateOwnerType.VENDOR:
            currentAddOns = vendorAddOns;
            break;
          case RateOwnerType.DRIVER:
            currentAddOns = driverAddOns;
            break;
          default:
            currentAddOns = [];
        }

        fetchRateAddOnAssignments(rate.id, selectedRateOwnerType, currentAddOns);
      }
    }
  };

  const getCurrentAddOns = (rateOwnerType: RateOwnerType): AddOnAssignmentSchema[] => {
    switch (rateOwnerType) {
      case RateOwnerType.CUSTOMER:
        return customerAddOns;
      case RateOwnerType.VENDOR:
        return vendorAddOns;
      case RateOwnerType.DRIVER:
        return driverAddOns;
      default:
        return [];
    }
  };

  const customerRateTypeSupportsQuantity = QUANTITY_AND_TIME_RATE_TYPES.some(
    (rateType) => rateType === customerRate?.type?.value,
  );
  const showsQuantity = scope === 'project' && customerRateTypeSupportsQuantity;

  // A quantity the new rate type cannot multiply is meaningless, and leaving it
  // behind would quote a line total the user never asked for. Only a genuine
  // post-mount type change clears it, so opening a saved card never silently
  // dirties the form.
  const previousQuantityRateTypeValueRef = useRef(customerRate?.type?.value);
  useEffect(() => {
    const customerTypeValue = customerRate?.type?.value;
    if (customerTypeValue === previousQuantityRateTypeValueRef.current) return;
    previousQuantityRateTypeValueRef.current = customerTypeValue;

    if (scope !== 'project' || showsQuantity) return;
    // Null, not undefined: `undefined` removes the key, and the field then
    // re-registers off the form's defaults — restoring the stale quantity the
    // moment the user picks a quantity-based rate type again.
    setValue(`siteRates.${index}.quantity`, null, { shouldDirty: true });
  }, [customerRate?.type?.value, index, scope, setValue, showsQuantity]);

  const customerActiveAddOnCount = countActiveAddOns(customerAddOns);
  const vendorActiveAddOnCount = countActiveAddOns(vendorAddOns);
  const driverActiveAddOnCount = countActiveAddOns(driverAddOns);

  const customerRateInput = (
    <RateAndTypeInput
      control={control}
      name={`siteRates.${index}.customerRate`}
      label={t('dispatch.job.customer_rate')}
      rateOwnerType={RateOwnerType.CUSTOMER}
      isCurrency
    />
  );

  const customerAddOnsBlock = (
    <>
      <Box sx={{ mt: 0.5 }}>
        <SurchargesAndAddOnsLink
          activeAddOnCount={customerActiveAddOnCount}
          dataTestId="freight-rate-section-menu-customer"
          onClick={(e) => handleMenuOpen(e, RateOwnerType.CUSTOMER)}
        />
      </Box>
      {customerAddOns.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <AddOnAssignmentCardV2
            addOnAssignments={customerAddOns}
            rateOwnerType={RateOwnerType.CUSTOMER}
            handleOpenAddOnDialog={() => handleAddOnDialogOpen(RateOwnerType.CUSTOMER)}
            orderDate={orderDate}
          />
        </Box>
      )}
    </>
  );

  const customerRateBlock = (
    <>
      {customerRateInput}
      {customerAddOnsBlock}
    </>
  );

  const vendorRateBlock = (
    <>
      <RateAndTypeInput
        control={control}
        name={`siteRates.${index}.vendorRate`}
        label={t('order.form.vendor_rate')}
        isCurrency
      />
      <Box sx={rateLinksRowSx}>
        <SurchargesAndAddOnsLink
          activeAddOnCount={vendorActiveAddOnCount}
          dataTestId="freight-rate-section-menu-vendor"
          onClick={(e) => handleMenuOpen(e, RateOwnerType.VENDOR)}
        />
        <PayCommissionControl
          control={control}
          name={`siteRates.${index}.vendorPayCommissionPercentage`}
          isCreating={!siteRateId}
          grossRate={vendorRate?.rate}
          rateType={vendorRate?.type?.value}
          inheritedPercentage={inheritedVendorCommission}
          savedGrossRate={initialVendorRate.gross}
          savedRateType={initialVendorRate.type}
          savedNetRate={initialVendorRate.net}
        />
      </Box>
      {vendorAddOns.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <AddOnAssignmentCardV2
            addOnAssignments={vendorAddOns}
            rateOwnerType={RateOwnerType.VENDOR}
            handleOpenAddOnDialog={() => handleAddOnDialogOpen(RateOwnerType.VENDOR)}
            orderDate={orderDate}
          />
        </Box>
      )}
    </>
  );

  const driverRateLabel = (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        justifyContent: 'space-between',
      }}
    >
      <Label>{t('dispatch.job.driver_rate')}</Label>
      <PseudoLink
        action={() => handleToggleVendorRateForDriver(!useVendorRateForDriver)}
        dataTestId="freight-rate-section-driver-rate-mode"
        linkColor={theme.semantic.linkText}
        title={
          useVendorRateForDriver
            ? t('project.form.driver_rate_edit_action')
            : t('project.form.driver_rate_match_vendor_action')
        }
        variant="action"
      />
    </Box>
  );

  const driverRateBlock = (
    <>
      {useVendorRateForDriver ? (
        <RateAndTypeInput
          control={control}
          // Bound to the vendor rate's name, so it needs an explicit id to
          // avoid colliding with the Vendor Rate box's id-by-name default.
          id={`siteRates.${index}.driverRate`}
          name={`siteRates.${index}.vendorRate`}
          label={driverRateLabel}
          isDisabled
          disabledReason={t('order.form.use_vendor_rate_for_driver')}
          isCurrency
        />
      ) : (
        <RateAndTypeInput
          control={control}
          name={`siteRates.${index}.driverRate`}
          label={driverRateLabel}
          isCurrency
        />
      )}
      {useVendorRateForDriver && (
        <Typography
          variant="caption"
          sx={(theme) => ({ color: theme.palette.text.secondary, display: 'block' })}
        >
          {t('project.form.driver_rate_matches_vendor_hint')}
        </Typography>
      )}
      <Box sx={rateLinksRowSx}>
        {!useVendorRateForDriver && (
          <SurchargesAndAddOnsLink
            activeAddOnCount={driverActiveAddOnCount}
            dataTestId="freight-rate-section-menu-driver"
            onClick={(e) => handleMenuOpen(e, RateOwnerType.DRIVER)}
          />
        )}
        <PayCommissionControl
          control={control}
          name={`siteRates.${index}.driverPayCommissionPercentage`}
          isCreating={!siteRateId}
          grossRate={driverPayRate?.rate}
          rateType={driverPayRate?.type?.value}
          inheritedPercentage={inheritedDriverCommission}
          savedGrossRate={initialDriverPayRate.gross}
          savedRateType={initialDriverPayRate.type}
          savedNetRate={initialDriverPayRate.net}
        />
      </Box>
      {!useVendorRateForDriver && driverAddOns.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <AddOnAssignmentCardV2
            addOnAssignments={driverAddOns}
            rateOwnerType={RateOwnerType.DRIVER}
            handleOpenAddOnDialog={() => handleAddOnDialogOpen(RateOwnerType.DRIVER)}
            orderDate={orderDate}
          />
        </Box>
      )}
    </>
  );

  return (
    <>
      {scope === 'project' ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>{customerRateInput}</Grid>
          <Grid size={{ xs: 6 }}>
            {showsQuantity && (
              <NumberInput
                control={control}
                name={`siteRates.${index}.quantity`}
                label={t('project.form.quoted_quantity')}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12 }}>{customerAddOnsBlock}</Grid>
          <Grid size={{ xs: 12 }}>{vendorRateBlock}</Grid>
          <Grid size={{ xs: 12 }}>{driverRateBlock}</Grid>
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>{customerRateBlock}</Box>
          <Box>{vendorRateBlock}</Box>
          <Box>{driverRateBlock}</Box>
        </Box>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        // Matches the Contact Type combobox's Autocomplete listbox
        // (src/components/FormFields/Parts/SelectOption.tsx) — this menu
        // previously carried zero style overrides, so it rendered with
        // MUI's own defaults (elevation-8 shadow, ~36px rows, gray hover,
        // and a plain MenuItem's raw 16px text — Contact Type's own
        // option label renders through a Typography body2, which this
        // theme sets to 12px, not MUI's untouched MenuItem default).
        elevation={4}
        sx={{
          '& .MuiMenuItem-root': {
            minHeight: '48px',
            px: theme.spacing(1.5),
            fontSize: '12px',
            fontWeight: 500,
            borderLeft: '3px solid transparent',
            '&:hover, &.Mui-selected, &.Mui-focusVisible': {
              backgroundColor: alpha(theme.brandV2.colors.treadYellow, 0.15),
              borderLeftColor: theme.brandV2.colors.treadYellow,
            },
          },
        }}
      >
        <MenuItem onClick={handleAddOnFromMenu}>{t('common.add_on')}</MenuItem>
        <MenuItem onClick={handleRateCardFromMenu}>
          {t('dispatch.site_rates.use_rate_card')}
        </MenuItem>
      </Menu>
      {selectedRateOwnerType && (
        <SelectRateCardDialog
          open={isSelectRateCardDialogOpen}
          onClose={handleCloseSelectRateCardDialog}
          onSelect={handleRateSelect}
          fetchRates={fetchRates}
          rateOwnerType={selectedRateOwnerType}
        />
      )}
      {selectedRateOwnerType && (
        <AutomaticRateAddOnDialog
          open={isAddOnDialogOpen}
          onClose={handleAddOnDialogClose}
          onSave={handleAddOnSave}
          rateOwnerType={selectedRateOwnerType}
          initialAddOns={getCurrentAddOns(selectedRateOwnerType)}
        />
      )}
    </>
  );
};

export default FreightRatesSection;
